/**
 * Bounded Context: Pyodide Bridge — WebLLM Runtime (TS-side)
 *
 * HLOS-90 — Frontend TS binding para WebLLMRuntime (Pyodide bridge).
 *
 * Este módulo expõe o objeto `webllm_runtime` que o Python
 * `hlos.llm_proxy.webllm_runtime.WebLLMRuntimeMLC` busca via
 * `pyodide.globals.get("webllm_runtime")` (ver
 * hlos/llm_proxy/webllm_runtime.py:181-242).
 *
 * Contrato (espelhado do lado Python):
 *   - load_model(model_id)          -> Promise<{ load_time_s: number, model_id: string }>
 *   - generate(prompt, max_tokens)  -> Promise<string>
 *   - is_loaded()                   -> Promise<boolean>
 *   - get_runtime_info()            -> Promise<WebLLMRuntimeInfo>
 *
 * Stack:
 *   - @mlc-ai/web-llm (dyn import para manter bundle inicial ≤ 50KB gzipped)
 *   - WebGPU (browser API; runtime real só funciona em devices com NPU)
 *
 * Constitucional:
 *   - CL-07 (local-first): zero rede para dados sensíveis; LLM roda 100% no browser.
 *   - CL-08 (ética fechada): `master_key` nunca exposto; LLM_ALLOWED_FIELDS enforced
 *     (defesa em profundidade: re-checado aqui mesmo após PrivacyBoundaryLayer Python-side).
 *   - Princípio XI (Companion ≠ LLM): runtime é infrastructure, não toca UX;
 *     zero menção a provider/modelo em logs user-facing (apenas dev console).
 *   - CC-04: zero spinner artificial; mensagem de load é texto neutro.
 *
 * Smoke test: tools/webllm_smoke_test.py valida o bridge com mock de pyodide.
 */

import {
  assertNoForbiddenBoundary,
  ConstitutionalViolationError,
} from "./flows";
import type { PyodideLike } from "./bridge";

// ─────────────────────────────────────────────────────────────────────────────
// Types — espelham hlos/llm_proxy/webllm_runtime.py dataclasses
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuração para inicializar o WebLLMRuntime.
 *
 * Espelha o que o Python passa como `model_id` em
 * `WebLLMRuntimeMLC.load_model(model_id)`.
 */
export interface WebLLMRuntimeConfig {
  /** Identificador do modelo (ex: "Llama-3.2-3B-Instruct-q4f16_1-MLC"). */
  readonly model_id: string;
}

/**
 * Resultado de WebLLMRuntimeMLC.load_model() (espelha Python `info` dict).
 *
 * O Python normaliza via `info.get("load_time_s", 0.0)` (ver
 * hlos/llm_proxy/webllm_runtime.py:210).
 */
export interface WebLLMRuntimeLoadResult {
  /** Tempo de load em segundos. */
  readonly load_time_s: number;
  /** Modelo carregado (echo de model_id para auditoria). */
  readonly model_id: string;
}

/**
 * Info runtime retornada por get_runtime_info() (espelha Python dict).
 *
 * O Python `WebLLMRuntimeMLC.get_runtime_info()` sobrescreve este shape,
 * mas o TS-side retorna este contrato para consistência.
 */
export interface WebLLMRuntimeInfo {
  /** Tipo de runtime. */
  readonly runtime: "webllm" | "llamacpp" | "mock";
  /** Modelo carregado (null se ainda não carregado). */
  readonly model_id: string | null;
  /** Se o modelo está carregado em memória. */
  readonly loaded: boolean;
  /** Tempo de load em segundos (null se ainda não carregado). */
  readonly load_time_s: number | null;
  /** Stub para CI ou runtime real. */
  readonly stub_or_real: "stub" | "real";
}

/**
 * HLOS-178 — Erro lançado quando WebGPU não está disponível no browser.
 *
 * Distinct from generic Error porque o caller (`load_model`) precisa
 * tomar uma decisão diferente: em vez de propagar o erro (que quebraria
 * o pipeline), faz fallback gracioso (CC-05: zero penalidade em erro).
 */
export class WebGPUUnavailableError extends Error {
  constructor(message = "WebGPU indisponível neste browser; device sem NPU.") {
    super(message);
    this.name = "WebGPUUnavailableError";
  }
}

/**
 * Interface do objeto que o Python espera via
 * `pyodide.globals.get("webllm_runtime")`.
 */
export interface WebLLMRuntimeBinding {
  load_model(model_id: string): Promise<WebLLMRuntimeLoadResult>;
  generate(prompt: string, max_tokens: number): Promise<string>;
  is_loaded(): Promise<boolean>;
  get_runtime_info(): Promise<WebLLMRuntimeInfo>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Runtime state (singleton, escopado a este módulo)
// ─────────────────────────────────────────────────────────────────────────────

/** Modelo carregado em memória. */
let _loaded = false;

/** ID do modelo carregado. */
let _model_id: string | null = null;

/** Tempo de load em segundos (medido por performance.now). */
let _load_time_s: number | null = null;

/** Engine do @mlc-ai/web-llm (lazy-loaded; tipado como unknown para evitar acoplar types da lib). */
let _engine: unknown | null = null;

/** Última mensagem de erro (para is_loaded/get_runtime_info). */
let _last_error: string | null = null;

// ─────────────────────────────────────────────────────────────────────────────
// Logging (dev-only — Princípio XI: zero provider name em UX/copy/logs prod)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Log apenas em dev. Em prod, silêncio absoluto (Princípio XI: nada de
 * "loading MLC model..." em logs user-facing).
 */
function devLog(message: string, meta?: Record<string, unknown>): void {
  if (typeof import.meta === "undefined") return;
  const env = (import.meta as { env?: { DEV?: boolean } }).env;
  if (env?.DEV !== true) return;
  // eslint-disable-next-line no-console
  console.debug(`[WebLLMRuntime] ${message}`, meta ?? {});
}

// ─────────────────────────────────────────────────────────────────────────────
// Privacy boundary (CL-07, CL-08) — reusa helper de flows.ts
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Valida prompt antes de enviar ao LLM (defesa em profundidade).
 *
 * O Python `PrivacyBoundaryLayer` já sanitiza; este check é cinto + suspensório
 * para garantir que mesmo se o caller upstream falhar, o runtime local
 * rejeita payloads com forbidden strings.
 *
 * @throws ConstitutionalViolationError se prompt contém forbidden strings.
 */
function assertPromptPrivacy(prompt: string): void {
  // Envelopa prompt num objeto para reusar o type guard existente
  // (assertNoForbiddenBoundary espera um payload com chaves/valores).
  assertNoForbiddenBoundary({ prompt }, "webllm_runtime.generate");
}

// ─────────────────────────────────────────────────────────────────────────────
// @mlc-ai/web-llm integration (dynamic import — CL-07 bundle budget)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Carrega @mlc-ai/web-llm dinamicamente e cria o engine.
 *
 * Dynamic import para manter o bundle inicial ≤ 50KB gzipped
 * (CL-07 + ADR-0019). @mlc-ai/web-llm é ~50MB, só baixa no primeiro
 * `load_model()` (cold start ~1-5s em devices M-series).
 *
 * HLOS-197: specifier literal (sem `@vite-ignore`) E sem variável.
 * Vite/Rollup resolve o import em build-time, cria chunk separado
 * `webllm-deps` (manualChunks no vite.config.ts:54-59) e SUBSTITUI
 * `import("@mlc-ai/web-llm")` por `import("/assets/webllm-deps-[hash].js")`.
 *
 * Crítico: o specifier literal + sem @vite-ignore é necessário porque
 * este módulo é executado dentro do Pyodide runtime (via
 * `js.webllm_runtime.load_model()`). Pyodide NÃO consegue resolver
 * bare specifiers como `@mlc-ai/web-llm` — apenas URLs absolutas ou
 * relativas. O caminho do chunk Vite (`/assets/webllm-deps-[hash].js`)
 * é resolvido pelo JS engine do Pyodide (igual ao browser normal).
 *
 * HLOS-194 (original): usava `@vite-ignore` + specifier via
 * variável para evitar bundling. Resultado: chunk `webllm-deps` NÃO era
 * criado, e o specifier literal `"@mlc-ai/web-llm"` falhava em Pyodide
 * com `TypeError: Failed to resolve module specifier`.
 *
 * @throws Error se WebGPU indisponível ou @mlc-ai/web-llm não exporta
 *         `CreateMLCEngine`.
 */
async function loadWebLLMEngine(modelId: string): Promise<unknown> {
  // HLOS-197: specifier literal (sem variável, sem @vite-ignore).
  // Vite cria chunk `webllm-deps-[hash].js` e substitui o import pelo
  // caminho do chunk. O Pyodide runtime consegue resolver `/assets/...`.
  const mod = (await import("@mlc-ai/web-llm")) as {
    CreateMLCEngine: (
      model_id: string,
      opts?: { initProgressCallback?: (p: { progress: number }) => void },
    ) => Promise<unknown>;
  };

  if (typeof mod.CreateMLCEngine !== "function") {
    throw new Error("@mlc-ai/web-llm missing CreateMLCEngine export");
  }

  // Verifica WebGPU antes de tentar criar engine.
  // HLOS-178: throw WebGPUUnavailableError (subclasse específica) em vez
  // de Error genérico. Caller (`load_model`) detecta via instanceof e
  // faz fallback gracioso (CC-05: zero penalidade em erro).
  if (typeof navigator === "undefined" || !("gpu" in navigator)) {
    throw new WebGPUUnavailableError();
  }

  return await mod.CreateMLCEngine(modelId, {
    initProgressCallback: (p: { progress: number }) => {
      devLog("load progress", { progress: p.progress });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Binding implementation
// ─────────────────────────────────────────────────────────────────────────────

/** Implementação concreta do WebLLMRuntimeBinding. */
const webllmRuntime: WebLLMRuntimeBinding = {
  async load_model(modelId: string): Promise<WebLLMRuntimeLoadResult> {
    const startedAt = performance.now();
    _last_error = null;

    try {
      // Validação de input (fail fast).
      if (typeof modelId !== "string" || modelId.length === 0) {
        throw new Error("model_id must be non-empty string");
      }

      // Lazy load do @mlc-ai/web-llm (bundle budget).
      _engine = await loadWebLLMEngine(modelId);

      const elapsed_s = (performance.now() - startedAt) / 1000;
      _loaded = true;
      _model_id = modelId;
      _load_time_s = elapsed_s;

      devLog("load_model complete", {
        model_id: modelId,
        load_time_s: elapsed_s,
      });

      return { load_time_s: elapsed_s, model_id: modelId };
    } catch (e) {
      _loaded = false;
      _model_id = null;
      _load_time_s = null;
      _last_error = e instanceof Error ? e.message : String(e);
      devLog("load_model failed", { error: _last_error });
      // HLOS-178: WebGPU indisponível é fallback gracioso (CC-05: zero
      // penalidade em erro). NÃO re-throw — caller decide o que fazer
      // via is_loaded() / get_runtime_info(). Outros erros continuam throw.
      if (e instanceof WebGPUUnavailableError) {
        devLog("WebGPU unavailable — runtime disabled", {
          reason: e.message,
          note: "Caller should fallback to mock or skip LLM calls",
        });
        // Retorna load_time_s=0 (sentinel "no-op") e model_id eco.
        return { load_time_s: 0, model_id: modelId };
      }
      throw e;
    }
  },

  async generate(prompt: string, maxTokens: number): Promise<string> {
    // HLOS-178: se WebGPU indisponível (runtime desabilitado), retorna
    // string vazia + warning em vez de throw duro. CC-05: zero penalidade
    // em erro — o caller (Python) pode fazer fallback gracioso.
    if (
      !_loaded &&
      _last_error !== null &&
      _last_error.includes("WebGPU")
    ) {
      devLog("generate skipped — WebGPU unavailable", {
        note: "Returning empty string; caller should fallback",
      });
      return "";
    }
    if (!_loaded || _engine === null) {
      throw new Error("model not loaded; call load_model() first");
    }

    // CL-08: privacy boundary check (defesa em profundidade).
    // Python já sanitizou via PrivacyBoundaryLayer, mas TS reforça.
    try {
      assertPromptPrivacy(prompt);
    } catch (e) {
      if (e instanceof ConstitutionalViolationError) {
        devLog("generate rejected: privacy violation", {
          violations: e.violations,
        });
      }
      throw e;
    }

    if (typeof prompt !== "string" || prompt.length === 0) {
      throw new Error("prompt must be non-empty string");
    }
    if (typeof maxTokens !== "number" || !Number.isFinite(maxTokens) || maxTokens <= 0) {
      throw new Error("max_tokens must be positive finite number");
    }

    // @mlc-ai/web-llm expõe engine.chat.completions.create() (chat API)
    // ou engine.completion.create() (completion API). Duck-typing para
    // evitar acoplar a versão exata da lib.
    const engine = _engine as {
      chat?: {
        completions: {
          create: (opts: unknown) => Promise<{
            choices: Array<{ message?: { content?: string }; text?: string }>;
          }>;
        };
      };
      completion?: {
        create: (opts: unknown) => Promise<{
          choices: Array<{ text?: string }>;
        }>;
      };
    };

    let text: string;
    if (engine.chat?.completions?.create) {
      const result = await engine.chat.completions.create({
        messages: [{ role: "user", content: prompt }],
        max_tokens: maxTokens,
      });
      const first = result.choices[0];
      text = first?.message?.content ?? "";
    } else if (engine.completion?.create) {
      const result = await engine.completion.create({
        prompt,
        max_tokens: maxTokens,
      });
      const first = result.choices[0];
      text = first?.text ?? "";
    } else {
      throw new Error("@mlc-ai/web-llm engine missing chat/completion API");
    }

    devLog("generate complete", {
      prompt_len: prompt.length,
      response_len: text.length,
    });
    return text;
  },

  async is_loaded(): Promise<boolean> {
    return _loaded;
  },

  async get_runtime_info(): Promise<WebLLMRuntimeInfo> {
    return {
      runtime: "webllm",
      model_id: _model_id,
      loaded: _loaded,
      load_time_s: _load_time_s,
      stub_or_real: "real",
    };
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Install (registra como global no Pyodide)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Instala `webllm_runtime` como global no Pyodide.
 *
 * O Python `WebLLMRuntimeMLC` busca via `bridge.globals.get("webllm_runtime")`
 * (bridge = módulo `pyodide` em Python). Pyodide expõe a main module globals
 * via `pyodide.globals`, então precisamos setar lá.
 *
 * Estratégia dupla (robusta):
 *   1. Tenta `pyodide.globals.set("webllm_runtime", webllmRuntime)` —
 *      API padrão de Pyodide 0.24+; funciona em produção.
 *   2. Fallback: seta em `globalThis.webllm_runtime` e roda Python para
 *      aliasar via `import js; webllm_runtime = js.webllm_runtime`.
 *      Útil se `globals.set` não existir (mock mode em testes).
 *
 * Idempotente: chamar 2x é no-op (o mesmo objeto é registrado).
 *
 * @param pyodide Instância PyodideLike (de `initPyodide()`).
 * @returns O binding instalado (para uso programático também).
 */
export function installWebLLMRuntime(
  pyodide: PyodideLike,
): WebLLMRuntimeBinding {
  // 1. Set no globalThis (sempre; baixo custo; usado pelo fallback Python).
  (globalThis as { webllm_runtime?: WebLLMRuntimeBinding }).webllm_runtime =
    webllmRuntime;

  // 2. Tenta pyodide.globals.set (Pyodide real).
  const globalsWithSet = pyodide.globals as {
    set?: (name: string, value: unknown) => void;
  };
  if (typeof globalsWithSet.set === "function") {
    globalsWithSet.set("webllm_runtime", webllmRuntime);
    devLog("install via globals.set", { note: "Pyodide real path" });
  } else {
    // 3. Fallback: alias em Python globals via `import js`.
    //    O Python `bridge.globals.get("webllm_runtime")` lê de Python globals;
    //    após este runPython, a var existe lá.
    void pyodide.runPythonAsync(
      "import js as _hlos_js; webllm_runtime = _hlos_js.webllm_runtime",
    );
    devLog("install via import js alias", { note: "fallback path" });
  }

  return webllmRuntime;
}

/** Expõe o binding para testes e uso programático fora de Pyodide. */
export { webllmRuntime };

/**
 * Reseta estado interno do runtime (apenas para testes).
 *
 * @internal
 */
export function _resetWebLLMRuntimeForTesting(): void {
  _loaded = false;
  _model_id = null;
  _load_time_s = null;
  _engine = null;
  _last_error = null;
}
